import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Row, Nav, NavItem, NavLink } from 'reactstrap';
import { fetchCompanyList } from 'components/companyList/redux/dispatcher';
import { fetchMachineListByCompanyId, fetchMachineListForBulkUpload } from 'components/report/redux/dispatcher';
import { fetchDailyOilConsumptionReport, fetchYearlyOilConsumptionReport, fetchDailyCoolantPhReport } from './redux/dispatcher';
import ReportDayWiseView from 'components/report/ReportDayWiseView';
import ReportYearlyWiseView from 'components/report/ReportYearlyWiseView';
import ReportPhConc from 'components/report/ReportPhConc';
import Report from 'components/report/Report';
import BulkUploadReport from 'components/report/BulkUploadReport';
import NotificationReport from 'components/report/NotificationReport';
import Loader from 'commonComponents/Loader';
// import toast from 'react-hot-toast';
import { serverCall } from 'utils/serverCall';
import apiEndpoints from 'utils/apiList';

// Months helper
const months = [
    { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' },
    { value: 5, label: 'May' }, { value: 6, label: 'Jun' }, { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' }
];

const currentYear = new Date().getFullYear();

const MonthDayReport = () => {
    const dispatch = useDispatch();
    const companyListData = useSelector((state) => state.companyListReducer?.data?.data) || [];
    const machineListFromStore = useSelector((state) => state.reportReducer?.machineByCompanyIdforreport);
    const stableMachineList = useMemo(() => machineListFromStore || [], [machineListFromStore]);
    const reportState = useSelector((state) => state.monthDayReportReducer);
    // Shared resources
    const [oilList, setOilList] = useState([]);
    const [showReport, setShowReport] = useState(false);
    const [activeTab, setActiveTab] = useState('machine');

    // Notification report state
    const [notificationData, setNotificationData] = useState(null);
    const [notificationLoading, setNotificationLoading] = useState(false);

    // Scalar state variables (contextual to active tab)
    const [companyId, setCompanyId] = useState('');
    const [machineIds, setMachineIds] = useState([]); // machine tab only
    const [selectedOils, setSelectedOils] = useState([]); // oil tab only
    const [month, setMonth] = useState(1);
    const [year, setYear] = useState(currentYear);
    const [oilType, setOilType] = useState('water');
    const [viewType, setViewType] = useState('');
    const [machineSearch, setMachineSearch] = useState('');
    const [oilSearch, setOilSearch] = useState('');
    const [ri, setRi] = useState(false); // machine tab only (coolant pH)
    const [isAllSelected, setIsAllSelected] = useState(false);

    // Refs to persist independent tab state snapshots so editing one tab doesn't mutate the other
    const machineTabRef = useRef({
        companyId: '',
        machineIds: [],
        month: 1,
        year: currentYear,
        oilType: 'water',
        viewType: '',
        machineSearch: '',
        ri: false
    });
    const oilTabRef = useRef({
        companyId: '',
        selectedOils: [],
        month: 1,
        year: currentYear,
        oilType: 'coolant',
        viewType: '',
        oilSearch: ''
    });
    const notificationTabRef = useRef({
        companyId: '',
        machineIds: [],
        machineSearch: '',
        fromDate: '',
        toDate: '',
        // notificationType: '',
        // notificationStatus: ''
        notificationType: [],
        notificationStatus: []
    });

    // Notification filter states
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    // const [notificationType, setNotificationType] = useState('');
    // const [notificationStatus, setNotificationStatus] = useState('');
    const [notificationType, setNotificationType] = useState([]);
    const [notificationStatus, setNotificationStatus] = useState([]);

    const switchTab = (nextTab) => {
        if (nextTab === activeTab) return;
        
        // Clear all form state so that switching tabs resets the form
        setCompanyId('');
        setMachineIds([]);
        setSelectedOils([]);
        setMonth(1);
        setYear(currentYear);
        setOilType('water');
        setViewType('');
        setMachineSearch('');
        setOilSearch('');
        setRi(false);
        setIsAllSelected(false);
        setNotificationData(null);
        setFromDate('');
        setToDate('');
        setNotificationType([]);
        setNotificationStatus([]);
        setShowReport(false);
        
        setActiveTab(nextTab);
    };
    const filteredMachineList = useMemo(() => stableMachineList.filter(m => m?.name?.toLowerCase().includes(machineSearch.toLowerCase())), [stableMachineList, machineSearch]);
    const filteredOilList = useMemo(() => {
        return oilList.filter(o => {
            const matchesSearch = o?.brand_name?.toLowerCase().includes(oilSearch.toLowerCase());
            const matchesCompany = !companyId || o?.company_id === parseInt(companyId, 10);
            const matchesOilType = !oilType || oilType === 'water' || o?.oil_type?.toLowerCase() === oilType.toLowerCase();
            return matchesSearch && matchesCompany && matchesOilType;
        });
    }, [oilList, oilSearch, companyId, oilType]);

    useEffect(() => { dispatch(fetchCompanyList()); }, [dispatch]);

    useEffect(() => {
        if (companyId) {
            // Use fetchMachineListForBulkUpload for Machine Wise Report, Oil Wise Report and Bulk Upload tabs
            if (activeTab === 'oil' || activeTab === 'bulk' || activeTab === 'machine') {
                const apiOilType = oilType === 'water' ? '' : oilType;
                dispatch(fetchMachineListForBulkUpload(companyId, apiOilType));
            } else {
                dispatch(fetchMachineListByCompanyId(companyId));
            }
        } else {
            setMachineIds([]);
        }
    }, [companyId, dispatch, activeTab, oilType, viewType]);

    // Call GET_ALL_OIL API and console the response
    useEffect(() => {
        const callGetAllOilAPI = async () => {
            try {
                const response = await serverCall(
                    apiEndpoints.GET_ALL_OIL,
                    'GET',
                    dispatch
                );
                console.log('GET_ALL_OIL API Response:', response);
                if (response.success && response.data?.payload?.oilDataWithSerialNumber) {
                    setOilList(response.data.payload.oilDataWithSerialNumber);
                }
            } catch (error) {
                console.error('Error calling GET_ALL_OIL API:', error);
            }
        };

        callGetAllOilAPI();
    }, [dispatch]);

    // Fetch oils by company and oil type specifically for Monthly Oil Consumption Report
    useEffect(() => {
        const fetchOilsByCompanyAndType = async () => {
            if (activeTab === 'oil' && viewType === 'yearly_oil' && companyId && oilType && oilType !== 'water') {
                try {
                    const apiOilType = oilType.charAt(0).toUpperCase() + oilType.slice(1);
                    const response = await serverCall(
                        `${apiEndpoints.OIL_BY_COMPANY}/${companyId}?oil_type=${apiOilType}`,
                        'GET',
                        dispatch
                    );
                    console.log('OIL_BY_COMPANY API Response:', response);
                    if (response.success && response.data?.payload) {
                        setOilList(response.data.payload);
                    } else if (response.payload) {
                        setOilList(response.payload);
                    } else {
                        setOilList([]);
                    }
                } catch (error) {
                    console.error('Error calling OIL_BY_COMPANY API:', error);
                }
            }
        };

        fetchOilsByCompanyAndType();
    }, [activeTab, viewType, companyId, oilType, dispatch]);

    const toggleMachine = (id) => {
        setMachineIds(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
    };

    const toggleOil = (id) => {
        setSelectedOils(prev => prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]);
    };

    useEffect(() => { setIsAllSelected(machineIds.length > 0 && machineIds.length === stableMachineList.length); }, [machineIds, stableMachineList]);

    const [isAllOilsSelected, setIsAllOilsSelected] = useState(false);
    useEffect(() => { setIsAllOilsSelected(selectedOils.length > 0 && selectedOils.length === filteredOilList.length); }, [selectedOils, filteredOilList]);

    // Clear selected oils when company changes
    useEffect(() => {
        setSelectedOils([]);
    }, [companyId]);

    const toggleSelectAll = () => {
        if (isAllSelected) setMachineIds([]); else setMachineIds(stableMachineList.map(m => m.id || m.machine_id));
    };

    const toggleSelectAllOils = () => {
        if (isAllOilsSelected) setSelectedOils([]); else setSelectedOils(filteredOilList.map(o => o.oil_id));
    };

    // removed unused toggleDropdown function

    const handleNotificationGenerate = async () => {
        if (!companyId) {
            // toast.error('Select company');
            return;
        }
        if (machineIds.length === 0) {
            // toast.error('Select at least one machine');
            return;
        }

        setNotificationLoading(true);
        try {
            // Get serial numbers for selected machines
            const selectedMachines = stableMachineList.filter(m =>
                machineIds.includes(m.id || m.machine_id)
            );
            const serialNumbers = selectedMachines.map(m => m.serial_number).filter(Boolean);

            if (serialNumbers.length === 0) {
                // toast.error('No serial numbers found for selected machines');
                setNotificationLoading(false);
                return;
            }

            const serialNumbersString = serialNumbers.join(',');
            let url = `${apiEndpoints.NOTIFICATION_REPORT}?company_id=${companyId}&machine_id=${serialNumbersString}`;

            // Add optional filters
            if (fromDate) url += `&from_date=${fromDate}`;
            if (toDate) url += `&to_date=${toDate}`;
            // if (notificationType) url += `&type=${notificationType}`;
            // if (notificationStatus) url += `&status=${notificationStatus}`;
            if (notificationType.length > 0) url += `&type=${notificationType.join(',')}`;
            if (notificationStatus.length > 0) url += `&status=${notificationStatus.join(',')}`;

            const response = await serverCall(url, 'GET', dispatch);

            if (response.success) {
                setNotificationData(response.data);
                setShowReport(true);
                // toast.success('Notification report generated successfully');
            } else {
                // toast.error(response.message || 'Failed to generate report');
            }
        } catch (error) {
            console.error('Error generating notification report:', error);
            // toast.error('Failed to generate notification report');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleMachineDetailsGenerate = async () => {
        if (!companyId) return;
        setNotificationLoading(true);
        try {
            const url = `${apiEndpoints.REPORT_COMPANY_MACHINE_DETAILS}?company_id=${companyId}`;
            const response = await serverCall(url, 'GET', dispatch, {}, {}, 'blob');

            if (response.success && response.data) {
                const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `Company_Machine_Details_${companyId}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(downloadUrl);
            }
        } catch (error) {
            console.error('Error downloading machine details:', error);
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleGenerate = () => {
        if (activeTab === 'notification') {
            handleNotificationGenerate();
            return;
        }

        const temporal = viewType.startsWith('daily') ? 'daily' : 'yearly';
        const isWater = viewType.includes('water');
        const isCoolantPh = viewType === 'daily_coolant_ph';
        const effectiveOilType = isWater ? 'water' : (isCoolantPh ? 'coolant' : oilType);
        const isMonthlyOilReport = viewType === 'yearly_oil';

        if (!companyId) { return; }
        if (!viewType) { return; }

        // For Monthly Oil Consumption Report, check oil selection instead of machine selection
        if (isMonthlyOilReport) {
            if (selectedOils.length === 0) { return; }
        } else {
            if (machineIds.length === 0) { return; }
        }

        if (!effectiveOilType) { return; }

        if (isCoolantPh) {
            dispatch(fetchDailyCoolantPhReport({
                year: parseInt(year, 10),
                month: parseInt(month, 10),
                machine_ids: machineIds.map(id => parseInt(id, 10)),
                company_id: parseInt(companyId, 10),
                ri
            }));
        } else if (temporal === 'daily') {
            dispatch(fetchDailyOilConsumptionReport({
                year: parseInt(year, 10),
                month: parseInt(month, 10),
                machine_ids: machineIds.map(id => parseInt(id, 10)),
                oil_type: effectiveOilType,
                company_id: parseInt(companyId, 10)
            }));
        } else {
            // For Monthly Oil Consumption Report, use oil_ids instead of machine_ids
            // For Monthly Coolant Oil Consumption Report, use specific API endpoint
            const payload = {
                year: parseInt(year, 10),
                oil_type: effectiveOilType,
                company_id: parseInt(companyId, 10)
            };

            if (isMonthlyOilReport) {
                payload.oil_ids = selectedOils.map(id => parseInt(id, 10));
            } else {
                payload.machine_ids = machineIds.map(id => parseInt(id, 10));
                // Set flag for monthly coolant oil consumption report
                if (viewType === 'yearly_coolant') {
                    payload.isMonthlyCoolant = true;
                }
            }

            dispatch(fetchYearlyOilConsumptionReport(payload));
        }
        setShowReport(true);
    };

    const handleBack = () => {
        // Hide report view and clear all field states
        setShowReport(false);

        // Clear notification data
        setNotificationData(null);

        // Clear all form fields
        setCompanyId('');
        setMachineIds([]);
        setSelectedOils([]);
        setMonth(1);
        setYear(currentYear);
        setOilType('water');
        setViewType('');
        setMachineSearch('');
        setOilSearch('');
        setRi(false);
        setIsAllSelected(false);

        // Reset both tab refs to default values
        machineTabRef.current = {
            companyId: '',
            machineIds: [],
            month: 1,
            year: currentYear,
            oilType: 'water',
            viewType: '',
            machineSearch: '',
            ri: false
        };
        oilTabRef.current = {
            companyId: '',
            selectedOils: [],
            month: 1,
            year: currentYear,
            oilType: 'coolant',
            viewType: '',
            oilSearch: ''
        };
        notificationTabRef.current = {
            companyId: '',
            machineIds: [],
            machineSearch: ''
        };

        // Reset to machine tab
        setActiveTab('machine');
    };

    const temporalView = viewType.startsWith('daily') ? 'daily' : 'yearly';
    // Water reports (daily or monthly) should hide oil type selector and force payload oil_type = 'water'
    if (activeTab !== 'fluid' && activeTab !== 'bulk' && activeTab !== 'machine_details' && reportState.isLoading && showReport && ((!reportState.dailyData && temporalView === 'daily') || (!reportState.yearlyData && temporalView === 'yearly'))) return <Loader />;

    return (
        <>
            {((activeTab === 'fluid' || activeTab === 'bulk' || activeTab === 'machine_details') || (activeTab === 'notification' && !showReport) || (activeTab !== 'fluid' && activeTab !== 'bulk' && activeTab !== 'notification' && activeTab !== 'machine_details' && !showReport)) && (
                <Container fluid className="mt-4">
                    <Col xl="10" className="p-0 mx-auto">
                        <Card className="bg-secondary shadow" style={{ border: 'none' }}>
                            <CardHeader className="bg-white border-0">
                                <Row className="align-items-center">
                                    <Col className="text-center">
                                        <h3 className="mb-0">Reports</h3>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody style={{ backgroundColor: '#f8f9fe' }}>
                                {/* Tabs for Machine wise / Oil wise */}
                                <Row>
                                    <Col md="12" className="mb-2">
                                        <Nav tabs className="custom-nav-tabs">
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'machine' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'machine') {
                                                            switchTab('machine');
                                                        }
                                                    }}
                                                >
                                                    Machine Wise Report
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'oil' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'oil') {
                                                            switchTab('oil');
                                                        }
                                                    }}
                                                >
                                                    Oil Wise Report
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'fluid' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'fluid') {
                                                            switchTab('fluid');
                                                        }
                                                    }}
                                                >
                                                    Fluid Handler Data
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'bulk' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'bulk') {
                                                            switchTab('bulk');
                                                        }
                                                    }}
                                                >
                                                    Bulk Upload
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'notification' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'notification') {
                                                            switchTab('notification');
                                                        }
                                                    }}
                                                >
                                                    Notifications
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    className={activeTab === 'machine_details' ? 'active' : ''}
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                    onClick={() => {
                                                        if (activeTab !== 'machine_details') {
                                                            switchTab('machine_details');
                                                        }
                                                    }}
                                                >
                                                    Machine Details
                                                </NavLink>
                                            </NavItem>
                                        </Nav>
                                    </Col>
                                </Row>
                                {activeTab === 'fluid' ? (
                                    // Render the Fluid Handler Data form (Report.js)
                                    <Row>
                                        <Col md="12">
                                            <Report />
                                        </Col>
                                    </Row>
                                ) : activeTab === 'bulk' ? (
                                    // Render the Bulk Upload Report form (BulkUploadReport.js)
                                    <Row>
                                        <Col md="12">
                                            <BulkUploadReport />
                                        </Col>
                                    </Row>
                                ) : activeTab === 'machine_details' ? (
                                    <>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Select Company<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={companyId}
                                                        onChange={e => {
                                                            setCompanyId(e.target.value);
                                                        }}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        <option value="">Select Company</option>
                                                        {companyListData.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col className="text-center mt-3">
                                                <Button
                                                    style={{ background: 'rgb(14, 61, 13)' }}
                                                    disabled={!companyId || notificationLoading}
                                                    onClick={handleMachineDetailsGenerate}
                                                >
                                                    {notificationLoading ? 'Generating...' : 'Generate'}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>
                                ) : activeTab === 'notification' ? (
                                    // Render the Notification Report form fields
                                    <>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Select Company<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={companyId}
                                                        onChange={e => {
                                                            setCompanyId(e.target.value);
                                                            setMachineIds([]);
                                                            setFromDate('');
                                                            setToDate('');
                                                            setNotificationType('');
                                                            setNotificationStatus('');
                                                        }}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        <option value="">Select Company</option>
                                                        {companyListData.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="12">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Machines {machineIds.length > 0 && `(${machineIds.length} Selected)`}<span className='text-red'>*</span></label>
                                                    <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 300, overflowY: 'auto' }}>
                                                        {!companyId ? (
                                                            <div className="text-muted text-center" style={{ padding: '20px', fontSize: '14px' }}>
                                                                Select company to see machine list
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="mb-2">
                                                                    <Input
                                                                        placeholder="Search Machine"
                                                                        value={machineSearch}
                                                                        onChange={e => setMachineSearch(e.target.value)}
                                                                        style={{ padding: '4px 8px', fontSize: '14px' }}
                                                                    />
                                                                </div>
                                                                <div className="">
                                                                    <div className="custom-control custom-checkbox">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id="selectAllMachinesNotification"
                                                                            checked={isAllSelected}
                                                                            onChange={toggleSelectAll}
                                                                        />
                                                                        <label
                                                                            className="custom-control-label"
                                                                            htmlFor="selectAllMachinesNotification"
                                                                            style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                                        >
                                                                            {isAllSelected ? `Clear All (${filteredMachineList.length})` : `Select All (${filteredMachineList.length})`}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                                                    {filteredMachineList.map(m => {
                                                                        const id = m.id || m.machine_id;
                                                                        return (
                                                                            <div key={id} className="custom-control custom-checkbox">
                                                                                <input type="checkbox" className="custom-control-input" id={`mach-notif-${id}`} value={id} checked={machineIds.includes(id)} onChange={() => toggleMachine(id)} />
                                                                                <label className="custom-control-label" htmlFor={`mach-notif-${id}`} style={{ fontSize: '14px' }}>{m.name} ({m.serial_number || id})</label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                {filteredMachineList.length === 0 && <div className="text-muted small">No machines</div>}
                                                            </>
                                                        )}
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Start Date</label>
                                                    <Input
                                                        type="date"
                                                        value={fromDate}
                                                        onChange={e => setFromDate(e.target.value)}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">End Date</label>
                                                    <Input
                                                        type="date"
                                                        value={toDate}
                                                        onChange={e => setToDate(e.target.value)}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Type</label>
                                                    <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 200, overflowY: 'auto' }}>
                                                        <div className="mb-2">
                                                            <div className="custom-control custom-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    className="custom-control-input"
                                                                    id="selectAllTypes"
                                                                    checked={notificationType && notificationType.length === 3}
                                                                    onChange={() => {
                                                                        if (notificationType && notificationType.length === 3) {
                                                                            setNotificationType([]);
                                                                        } else {
                                                                            setNotificationType(['danger', 'alert', 'warning']);
                                                                        }
                                                                    }}
                                                                />
                                                                <label
                                                                    className="custom-control-label"
                                                                    htmlFor="selectAllTypes"
                                                                    style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                                >
                                                                    {notificationType && notificationType.length === 3 ? 'Clear All' : 'Select All'}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="type-danger"
                                                                value="danger"
                                                                checked={notificationType && notificationType.includes('danger')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setNotificationType([...notificationType, 'danger']);
                                                                    } else {
                                                                        setNotificationType(notificationType.filter(t => t !== 'danger'));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="custom-control-label" htmlFor="type-danger" style={{ fontSize: '14px' }}>Danger</label>
                                                        </div>
                                                        <div className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="type-alert"
                                                                value="alert"
                                                                checked={notificationType && notificationType.includes('alert')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setNotificationType([...notificationType, 'alert']);
                                                                    } else {
                                                                        setNotificationType(notificationType.filter(t => t !== 'alert'));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="custom-control-label" htmlFor="type-alert" style={{ fontSize: '14px' }}>Alert</label>
                                                        </div>
                                                        <div className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="type-warning"
                                                                value="warning"
                                                                checked={notificationType && notificationType.includes('warning')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setNotificationType([...notificationType, 'warning']);
                                                                    } else {
                                                                        setNotificationType(notificationType.filter(t => t !== 'warning'));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="custom-control-label" htmlFor="type-warning" style={{ fontSize: '14px' }}>Warning</label>
                                                        </div>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Status</label>
                                                    <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 200, overflowY: 'auto' }}>
                                                        <div className="mb-2">
                                                            <div className="custom-control custom-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    className="custom-control-input"
                                                                    id="selectAllStatus"
                                                                    checked={notificationStatus && notificationStatus.length === 2}
                                                                    onChange={() => {
                                                                        if (notificationStatus && notificationStatus.length === 2) {
                                                                            setNotificationStatus([]);
                                                                        } else {
                                                                            setNotificationStatus(['resolved', 'unresolved']);
                                                                        }
                                                                    }}
                                                                />
                                                                <label
                                                                    className="custom-control-label"
                                                                    htmlFor="selectAllStatus"
                                                                    style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                                >
                                                                    {notificationStatus && notificationStatus.length === 2 ? 'Clear All' : 'Select All'}
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="status-resolved"
                                                                value="resolved"
                                                                checked={notificationStatus && notificationStatus.includes('resolved')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setNotificationStatus([...notificationStatus, 'resolved']);
                                                                    } else {
                                                                        setNotificationStatus(notificationStatus.filter(s => s !== 'resolved'));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="custom-control-label" htmlFor="status-resolved" style={{ fontSize: '14px' }}>Resolved</label>
                                                        </div>
                                                        <div className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="status-unresolved"
                                                                value="unresolved"
                                                                checked={notificationStatus && notificationStatus.includes('unresolved')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setNotificationStatus([...notificationStatus, 'unresolved']);
                                                                    } else {
                                                                        setNotificationStatus(notificationStatus.filter(s => s !== 'unresolved'));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="custom-control-label" htmlFor="status-unresolved" style={{ fontSize: '14px' }}>Unresolved</label>
                                                        </div>
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {/* <Row>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Type</label>
                                                    <Input
                                                        type="select"
                                                        multiple
                                                        value={notificationType}
                                                        onChange={e => setNotificationType(Array.from(e.target.selectedOptions, option => option.value))}
                                                        style={{ padding: '6px 12px', fontSize: '14px', height: '100px' }}
                                                    >
                                                        <option value="danger">Danger</option>
                                                        <option value="alert">Alert</option>
                                                        <option value="warning">Warning</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Status</label>
                                                    <Input
                                                        type="select"
                                                        multiple
                                                        value={notificationStatus}
                                                        onChange={e => setNotificationStatus(Array.from(e.target.selectedOptions, option => option.value))}
                                                        style={{ padding: '6px 12px', fontSize: '14px', height: '100px' }}
                                                    >
                                                        <option value="resolved">Resolved</option>
                                                        <option value="unresolved">Unresolved</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row> */}
                                        <Row>
                                            <Col className="text-center">
                                                <Button
                                                    style={{ background: 'rgb(14, 61, 13)' }}
                                                    disabled={!companyId || machineIds.length === 0 || notificationLoading}
                                                    onClick={handleGenerate}
                                                >
                                                    {notificationLoading ? 'Generating...' : 'Generate'}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (<>
                                    {/* View Type dropdown changes based on active tab */}
                                    <Row>
                                        <Col md="12">
                                            <FormGroup className="mb-2">
                                                <label className="mb-1">View Type<span className='text-red'>*</span></label>
                                                <Input
                                                    type="select"
                                                    value={viewType}
                                                    onChange={e => {
                                                        const v = e.target.value;
                                                        setViewType(v);
                                                        setMachineIds([]);
                                                        setSelectedOils([]);
                                                        if (v.includes('water')) {
                                                            setOilType('water');
                                                        } else if (v === 'daily_coolant_ph') {
                                                            setOilType('coolant');
                                                        } else if (v === 'yearly_coolant') {
                                                            setOilType('coolant');
                                                        } else if (oilType === 'water') {
                                                            // leaving water context back to oil specific options
                                                            setOilType('coolant');
                                                        }
                                                    }}
                                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                                >
                                                    <option value="" disabled hidden>Select View Type</option>
                                                    {activeTab === 'machine' && (
                                                        <>
                                                            <option value="yearly_water">Monthly Water Consumption Report</option>
                                                            <option value="yearly_coolant">Monthly Coolant Oil Consumption Report</option>
                                                            <option value="daily_coolant_ph">Daily Coolant data report for pH and concentration</option>
                                                        </>
                                                    )}
                                                    {activeTab === 'oil' && (
                                                        <>
                                                            <option value="daily_oil">Daily Oil Consumption Report</option>
                                                            <option value="yearly_oil">Monthly Oil Consumption Report</option>
                                                        </>
                                                    )}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup className="mb-2">
                                                <label className="mb-1">Select Company<span className='text-red'>*</span></label>
                                                <Input
                                                    type="select"
                                                    value={companyId}
                                                    onChange={e => { setCompanyId(e.target.value); setMachineIds([]); }}
                                                    style={{ padding: '6px 12px', fontSize: '14px' }}
                                                >
                                                    <option value="">Select Company</option>
                                                    {companyListData.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        {viewType && !viewType.includes('water') && (
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Oil Type<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={oilType}
                                                        onChange={e => {
                                                            if (viewType === 'daily_coolant_ph' || viewType === 'yearly_coolant') return; // locked to coolant
                                                            setOilType(e.target.value);
                                                            setMachineIds([]);
                                                            setSelectedOils([]);
                                                        }}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                        disabled={oilType === 'water' || viewType === 'daily_coolant_ph' || viewType === 'yearly_coolant'}
                                                    >
                                                        <option value="coolant">Coolant</option>
                                                        {viewType !== 'daily_coolant_ph' && viewType !== 'yearly_coolant' && <option value="hydraulic">Hydraulic</option>}
                                                        {viewType !== 'daily_coolant_ph' && viewType !== 'yearly_coolant' && <option value="maintenance">Neat</option>}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        )}
                                        {/* When Monthly Water Consumption (yearly_water) is selected, show Year beside Company */}
                                        {viewType === 'yearly_water' && (
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Year<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={year}
                                                        onChange={e => setYear(e.target.value)}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        {Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i).map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        )}
                                    </Row>
                                    {temporalView === 'daily' && (
                                        <Row>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Month<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={month}
                                                        onChange={e => setMonth(e.target.value)}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Year<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={year}
                                                        onChange={e => setYear(e.target.value)}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        {Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i).map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            {viewType === 'daily_coolant_ph' && (
                                                <Col md="12">
                                                    <FormGroup className="mb-2 d-flex align-items-center">
                                                        <div className="custom-control custom-switch">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="riToggle"
                                                                checked={ri}
                                                                onChange={(e) => setRi(e.target.checked)}
                                                            />
                                                            <label className="custom-control-label" htmlFor="riToggle" style={{ fontSize: '14px' }}>Include RI</label>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                    {viewType && temporalView === 'yearly' && viewType !== 'yearly_water' && (
                                        <Row>
                                            <Col md="6">
                                                <FormGroup className="mb-2">
                                                    <label className="mb-1">Year<span className='text-red'>*</span></label>
                                                    <Input
                                                        type="select"
                                                        value={year}
                                                        onChange={e => setYear(e.target.value)}
                                                        style={{ padding: '6px 12px', fontSize: '14px' }}
                                                    >
                                                        {Array.from({ length: currentYear - 1989 }, (_, i) => currentYear - i).map(y => (
                                                            <option key={y} value={y}>{y}</option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    )}
                                    <Row>
                                        <Col md="12">
                                            <FormGroup className="mb-2">
                                                <label className="mb-1">
                                                    {viewType === 'yearly_oil' ? `Oils ${selectedOils.length > 0 ? `(${selectedOils.length} Selected)` : ''}` : `Machines ${machineIds.length > 0 ? `(${machineIds.length} Selected)` : ''}`}
                                                    <span className='text-red'>*</span>
                                                </label>
                                                <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 300, overflowY: 'auto' }}>
                                                    {viewType === 'yearly_oil' ? (
                                                        // Oil List for Monthly Oil Consumption Report
                                                        !companyId ? (
                                                            <div className="text-muted text-center" style={{ padding: '20px', fontSize: '14px' }}>
                                                                Select company to see oil list
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="mb-2">
                                                                    <Input
                                                                        placeholder="Search Oil Brand"
                                                                        value={oilSearch}
                                                                        onChange={e => setOilSearch(e.target.value)}
                                                                        style={{ padding: '4px 8px', fontSize: '14px' }}
                                                                    />
                                                                </div>
                                                                <div className="">
                                                                    <div className="custom-control custom-checkbox">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id="selectAllOils"
                                                                            checked={isAllOilsSelected}
                                                                            onChange={toggleSelectAllOils}
                                                                        />
                                                                        <label
                                                                            className="custom-control-label"
                                                                            htmlFor="selectAllOils"
                                                                            style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                                        >
                                                                            {isAllOilsSelected ? `Clear All (${filteredOilList.length})` : `Select All (${filteredOilList.length})`}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                                                    {filteredOilList.map(oil => {
                                                                        const id = oil.oil_id;
                                                                        console.log(oil, "oil")
                                                                        return (
                                                                            <div key={id} className="custom-control custom-checkbox">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="custom-control-input"
                                                                                    id={`oil-${id}`}
                                                                                    value={id}
                                                                                    checked={selectedOils.includes(id)}
                                                                                    onChange={() => toggleOil(id)}
                                                                                />
                                                                                <label className="custom-control-label" htmlFor={`oil-${id}`} style={{ fontSize: '14px' }}>
                                                                                    {oil.brand_name} ({oil.product_name?.length > 20 ? oil.product_name.substring(0, 20) + '...' : oil.product_name})
                                                                                </label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                {filteredOilList.length === 0 && <div className="text-muted small">No oils found for this company</div>}
                                                            </>
                                                        )
                                                    ) : (
                                                        // Machine List for other reports
                                                        !companyId ? (
                                                            <div className="text-muted text-center" style={{ padding: '20px', fontSize: '14px' }}>
                                                                Select company to see machine list
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="mb-2">
                                                                    <Input
                                                                        placeholder="Search Machine"
                                                                        value={machineSearch}
                                                                        onChange={e => setMachineSearch(e.target.value)}
                                                                        style={{ padding: '4px 8px', fontSize: '14px' }}
                                                                    />
                                                                </div>
                                                                <div className="">
                                                                    <div className="custom-control custom-checkbox">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="custom-control-input"
                                                                            id="selectAllMachines"
                                                                            checked={isAllSelected}
                                                                            onChange={toggleSelectAll}
                                                                        />
                                                                        <label
                                                                            className="custom-control-label"
                                                                            htmlFor="selectAllMachines"
                                                                            style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                                        >
                                                                            {isAllSelected ? `Clear All (${filteredMachineList.length})` : `Select All (${filteredMachineList.length})`}
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                                                    {filteredMachineList.map(m => {
                                                                        const id = m.id || m.machine_id; // backend field
                                                                        return (
                                                                            <div key={id} className="custom-control custom-checkbox">
                                                                                <input type="checkbox" className="custom-control-input" id={`mach-${id}`} value={id} checked={machineIds.includes(id)} onChange={() => toggleMachine(id)} />
                                                                                <label className="custom-control-label" htmlFor={`mach-${id}`} style={{ fontSize: '14px' }}>{m.name} ({m.serial_number || id})</label>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                                {filteredMachineList.length === 0 && <div className="text-muted small">No machines</div>}
                                                            </>
                                                        )
                                                    )}
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="text-center">
                                            <Button style={{ background: 'rgb(14, 61, 13)' }}
                                                disabled={
                                                    !viewType ||
                                                    !companyId ||
                                                    !year ||
                                                    (!oilType && !viewType.includes('water')) ||
                                                    (temporalView === 'daily' && !month) ||
                                                    (viewType === 'yearly_oil' ? selectedOils.length === 0 : machineIds.length === 0)
                                                }
                                                onClick={handleGenerate}>
                                                Generate
                                            </Button>
                                        </Col>
                                    </Row>
                                </>)}
                            </CardBody>
                        </Card>
                    </Col>
                </Container>
            )}
            {activeTab !== 'fluid' && activeTab !== 'bulk' && activeTab !== 'notification' && activeTab !== 'machine_details' && showReport && temporalView === 'daily' && viewType !== 'daily_coolant_ph' && (
                <ReportDayWiseView
                    onBack={handleBack}
                    dailyData={reportState.dailyData}
                    oilType={oilType}
                    month={month}
                    companyName={companyListData.find(c => c.id === parseInt(companyId, 10))?.company_name}
                />
            )}
            {activeTab !== 'fluid' && activeTab !== 'bulk' && activeTab !== 'notification' && activeTab !== 'machine_details' && showReport && viewType === 'daily_coolant_ph' && (
                <ReportPhConc
                    onBack={handleBack}
                    month={month}
                    year={year}
                    dailyData={reportState.dailyData?.data || []}
                    ri={ri}
                    companyName={companyListData.find(c => c.id === parseInt(companyId, 10))?.company_name}
                />
            )}
            {activeTab !== 'fluid' && activeTab !== 'bulk' && activeTab !== 'notification' && activeTab !== 'machine_details' && showReport && temporalView === 'yearly' && (
                <ReportYearlyWiseView
                    onBack={handleBack}
                    yearlyData={reportState.yearlyData}
                    companyName={companyListData.find(c => c.id === parseInt(companyId, 10))?.company_name}
                    reportYear={year}
                    oilType={oilType}
                    viewType={viewType}
                />
            )}
            {activeTab === 'notification' && showReport && notificationData && (
                <NotificationReport
                    onBack={handleBack}
                    notificationData={notificationData}
                    companyName={companyListData.find(c => c.id === parseInt(companyId, 10))?.company_name}
                    fromDate={fromDate}
                    toDate={toDate}
                    notificationType={notificationType}
                    notificationStatus={notificationStatus}
                />
            )}
        </>
    );
};

export default MonthDayReport;